import { format, parseISO, isValid } from 'date-fns';
import { ja } from 'date-fns/locale';
import * as Sentry from '@sentry/react';

const jstFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Date → JST YYYY-MM-DD。toISOString()はUTC変換でJST深夜帯にずれるため使用禁止。
 * @throws {RangeError} dateが無効な場合（Invalid Date）
 */
export function toLocalDateString(date: Date = new Date()): string {
  if (isNaN(date.getTime())) {
    throw new RangeError(
      `toLocalDateString: invalid Date received. Caller must validate input. Got: ${String(date)}`
    );
  }
  const parts = jstFormatter.formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;
  if (!y || !m || !d) {
    throw new Error(
      `toLocalDateString: unexpected formatToParts output. Parts: ${JSON.stringify(parts)}`
    );
  }
  return `${y}-${m}-${d}`;
}

/** Date → JST YYYYMMDD。予約番号・顧客コード生成用。 */
export function toLocalDateCompact(date: Date = new Date()): string {
  return toLocalDateString(date).replace(/-/g, '');
}

/**
 * 日付を指定されたフォーマットで文字列に変換
 */
export const formatDate = (
  date: Date | string,
  formatStr: string = 'yyyy/MM/dd'
): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) {
      return '';
    }
    return format(dateObj, formatStr, { locale: ja });
  } catch {
    return '';
  }
};

/**
 * 時刻を指定されたフォーマットで文字列に変換
 */
export const formatTime = (
  time: string,
  formatStr: string = 'HH:mm'
): string => {
  try {
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    return format(date, formatStr, { locale: ja });
  } catch {
    return time;
  }
};

/**
 * 日付と時刻を結合して文字列に変換
 */
export const formatDateTime = (date: Date | string, time?: string): string => {
  try {
    const dateStr = formatDate(date, 'yyyy/MM/dd');
    const timeStr = time ? ` ${formatTime(time)}` : '';
    return `${dateStr}${timeStr}`;
  } catch {
    return '';
  }
};

/**
 * 日本円フォーマッタ (Issue #2853)
 *
 * Backend の `decimal(10, 2)` カラムが JSON serialize 時に `"4160.00"` (string)
 * として返るため、`amount.toLocaleString('ja-JP')` ではなく Intl.NumberFormat
 * シングルトンを使い、`maximumFractionDigits: 0` で小数点を強制削除する。
 *
 * **サイレント `¥0` フォールバック禁止** (Issue #2855 レビュー指摘):
 * NaN/Infinity/非数文字列を `¥0` に落とすと決済画面 (PaymentManagement の
 * お釣り表示など RED LEVEL) でスタッフが「お釣りなし」と誤認するリスクが
 * あるため、`¥—` (半角 ¥ + em dash) を返して視覚的に異常値であることを
 * 示し、同時に Sentry/console に送信して可観測化する。
 */
const JPY_FORMATTER = new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
  maximumFractionDigits: 0,
});

/**
 * `formatCurrency` が NaN/Infinity/非数文字列を受け取った時に返す sentinel 値。
 * 呼び出し側 (ソート・合計計算・集計など) が「正常な金額表示か異常値か」を
 * 判定したい場合は、戻り値をこの定数と等値比較する。
 *
 * 例: `const display = formatCurrency(value); if (display === FORMAT_CURRENCY_FALLBACK) { ... }`
 */
export const FORMAT_CURRENCY_FALLBACK = '¥—' as const;
export const FORMAT_CURRENCY_FALLBACK_BARE = '—' as const;

/**
 * 通貨フォーマット (日本円)
 *
 * @returns `¥4,160` / `4,160` (NaN/Infinity の場合は `¥—` / `—` + Sentry 通知)
 */
export const formatCurrency = (
  amount: number | string,
  includeSymbol: boolean = true
): string => {
  // `Number('')` と `Number('   ')` は `0` を返すため、空文字/空白のみの
  // 文字列は明示的に NaN 扱いにする (意味的に「数値ではない」入力を
  // `¥0` と誤認させないため)。
  const isEmptyString = typeof amount === 'string' && amount.trim() === '';
  const numeric = isEmptyString
    ? NaN
    : typeof amount === 'string'
      ? Number(amount)
      : amount;
  if (!Number.isFinite(numeric)) {
    const message = `[formatCurrency] Invalid amount: ${JSON.stringify(amount)}`;
    console.error(message);
    // `@sentry/core` の `captureMessage` は init 未実行/DSN 未設定でも throw せず
    // no-op で済む設計 (`getCurrentScope().captureMessage` が空 scope で dry-run)。
    // 本プロジェクトの他の Sentry 呼び出し (stripeService, subscriptionService,
    // BookingSiteLayout 等) も try/catch なしで直呼びしており、ここでも不要。
    Sentry.captureMessage(message, { level: 'error' });
    return includeSymbol
      ? FORMAT_CURRENCY_FALLBACK
      : FORMAT_CURRENCY_FALLBACK_BARE;
  }
  const formatted = JPY_FORMATTER.format(numeric);
  // Intl.NumberFormat('ja-JP', {style:'currency', currency:'JPY'}) は全角 `￥`
  // (U+FFE5) を返すため、既存 API 契約 (半角 `¥` = U+00A5) を維持するために置換する。
  const normalized = formatted.replace('￥', '¥');
  return includeSymbol ? normalized : normalized.replace('¥', '');
};

/**
 * 期間を分単位で表示
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}分`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}時間${mins}分` : `${hours}時間`;
};

/**
 * 相対的な日付表示（今日、明日、昨日など）
 */
export const getRelativeDate = (date: Date): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  switch (diffDays) {
    case 0:
      return '今日';
    case 1:
      return '明日';
    case -1:
      return '昨日';
    default:
      if (diffDays > 0 && diffDays <= 7) {
        return `${diffDays}日後`;
      } else if (diffDays < 0 && diffDays >= -7) {
        return `${Math.abs(diffDays)}日前`;
      }
      return formatDate(date);
  }
};

/**
 * YYYY-MM-DD文字列の日付範囲からYYYY-MM-DD文字列の配列を生成
 * toLocalDateString()を使用してJST TZバグを回避
 */
export function generateDateRange(
  startDate: string,
  endDate: string
): string[] {
  const dates: string[] = [];
  const current = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  while (current <= end) {
    dates.push(toLocalDateString(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}
